import { ReactElement, SyntheticEvent, memo, useRef, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import { useIsScrolledToBottom } from '@bymason/util-hooks';
import { MasonTheme } from '@bymason-private/controller-v1-theme-augmentation';
import { Paper } from '../../mui/Paper';
import { ToggleButton } from '../../mui/ToggleButton';
import { ToggleButtonGroup } from '../../mui/ToggleButtonGroup';
import {
  FiMoreVertical,
  UxTableDensityComfortable,
  UxTableDensityCompact,
  UxTableDensityStandard,
} from '../Icons';
import { IconButton } from '../../mui/IconButton';
import { GridDensity } from '../../mui/DataGridPro';
import { ControllerSectionedMenu } from '../ControllerSectionedMenu';
import { Text } from '../Text';
import { CarouselSelector } from '../CarouselSelector';
import { OnFilterModelChange } from '../ControllerDataGrid';

export interface DataGridToggle {
  disablePageNext?: boolean;
  disablePagePrev?: boolean;
  headerContent?: JSX.Element;
  pagination?: boolean;
  page?: number;
  toggleLabel?: string;
  onDensityChange: (newDensity: GridDensity) => void;
  onFilterModelChange?: OnFilterModelChange;
  onPageChange?: (direction: 'next' | 'previous') => void;
}

export interface DataGridContainerProps {
  activeDensity: GridDensity;
  activeGridIndex: number;
  children: ReactElement | ReactElement[];
  className?: string;
  error?: boolean;
  gridToggles: DataGridToggle[];
  minToggleWidth?: number;
  onGridIndexChange: (newGridIndex: number) => void;
}

const useStyles = makeStyles((theme: MasonTheme) => ({
  paperRoot: {
    borderRadius: 2,
    boxShadow: 'none',
    transition: 'none',
    backgroundColor: 'transparent',
    '& .MasonDataGridContainer-root': {
      marginTop: theme.spacing(0.5),
      width: '100%',
      '& .MasonDataGridContainer-header': {
        alignItems: 'center',
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.grey[200]}`,
        borderBottom: 'none',
        borderRadius: theme.spacing(0.25),
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        color: theme.palette.grey[800],
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 0,
        padding: theme.spacing(1.5, 1.5, 1.5, 0.5),
        '&-error': {
          color: theme.palette.error.main,
        },
        '& .MuiToggleButtonGroup-root': {
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 0,
          marginRight: theme.spacing(2),
        },
        '& .MasonDataGridContainer-toggle': {
          fontSize: 15,
          '&.MuiToggleButton-root': {
            border: 'none',
            borderLeft: `1px solid ${theme.palette.grey[200]}`,
            borderRight: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 0,
            textTransform: 'none',
            '&:first-of-type': {
              borderLeft: 'none',
            },
            '&:last-of-type': {
              borderRight: 'none',
            },
          },
          '&.Mui-selected': {
            fontSize: 15,
          },
        },
        '& .MasonDataGridContainer-header-menuicon': {
          flexGrow: 0,
          marginRight: theme.spacing(0.5),
          textAlign: 'right',
        },
      },
      '& .MasonDataGridContainer-footer': {
        alignItems: 'center',
        backgroundColor: theme.palette.grey[50],
        border: `1px solid ${theme.palette.grey[200]}`,
        borderTop: 'none',
        borderRadius: theme.spacing(0.25),
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 0,
        padding: theme.spacing(1.5),
      },
    },
    '& .MasonDataGridContainer-header-menuitem-icon': {
      color: theme.palette.grey[700],
    },
  },
  paginationRoot: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    justifyContent: 'flex-end',
  },
  paginationLabelRoot: {
    flexGrow: 1,
    marginRight: theme.spacing(2),
    textAlign: 'right',
  },
  paginationLabelLeft: {
    ...theme.typography.h6,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
  },
  paginationActivePage: {
    ...theme.typography.h5,
  },
}));

export default memo(function DataGridContainer({
  activeDensity,
  activeGridIndex,
  children,
  className,
  error,
  gridToggles,
  minToggleWidth,
  onGridIndexChange,
}: DataGridContainerProps) {
  const classes = useStyles();
  const tableMenuButtonElem = useRef(null);
  const [isScrolledToBottom] = useIsScrolledToBottom(80);
  const [showDensityMenu, setShowDensityMenu] = useState(false);

  const activeGridToggle = gridToggles[activeGridIndex];
  const activePage = activeGridToggle.page;
  const disableActivePageNext = activeGridToggle.disablePageNext;
  const disableActivePagePrev = activeGridToggle.disablePagePrev;

  const handleToggleChange = (event: SyntheticEvent, newValue: number) => {
    onGridIndexChange(newValue || 0);
  };

  const handleDensityMenuClose = () => {
    setShowDensityMenu(false);
  };

  const handleDensityMenuClick = (density: GridDensity) => {
    activeGridToggle.onDensityChange(density);
    handleDensityMenuClose();
  };

  const handleNextClick = () => {
    if (activeGridToggle.onPageChange) {
      activeGridToggle.onPageChange('next');
    }
  };

  const handlePreviousClick = () => {
    if (activeGridToggle.onPageChange) {
      activeGridToggle.onPageChange('previous');
    }
  };

  const PaginationZone = () => (
    <div className={classes.paginationRoot}>
      <CarouselSelector
        disableNext={disableActivePageNext}
        disablePrevious={disableActivePagePrev}
        label={
          <div className={classes.paginationLabelRoot}>
            <Text className={classes.paginationLabelLeft}>Viewing page</Text>
            <Text className={classes.paginationActivePage}>{activePage}</Text>
          </div>
        }
        onNextClick={handleNextClick}
        onPreviousClick={handlePreviousClick}
      />
    </div>
  );

  return (
    <Paper className={classes.paperRoot}>
      <div className={clsx('MasonDataGridContainer-root', className)}>
        <div
          className={clsx('MasonDataGridContainer-header', error && 'MasonDataGridContainer-header-error')}
        >
          <div className='MasonDataGridContainer-header-menuicon'>
            <IconButton
              onClick={() => {
                setShowDensityMenu(true);
              }}
              ref={tableMenuButtonElem}
              size='medium'
            >
              <FiMoreVertical />
            </IconButton>
          </div>
          {gridToggles.length > 1 && (
            <ToggleButtonGroup exclusive onChange={handleToggleChange} value={activeGridIndex}>
              {gridToggles.map((gridToggle, idx) => (
                <ToggleButton
                  classes={{
                    root: 'MasonDataGridContainer-toggle',
                    selected: 'MasonDataGridContainer-toggle',
                  }}
                  key={`MasonDataGridContainerToggle-${idx}`}
                  style={{ minWidth: minToggleWidth }}
                  value={idx}
                >
                  {gridToggle.toggleLabel}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          {activeGridToggle.headerContent}
          {activeGridToggle.pagination && <PaginationZone />}
          <ControllerSectionedMenu
            anchorElem={tableMenuButtonElem.current}
            onClose={handleDensityMenuClose}
            onItemClick={handleDensityMenuClick}
            open={showDensityMenu}
            sections={[
              {
                caption: 'Table Row Density',
                items: [
                  {
                    id: 'compact',
                    icon: <UxTableDensityCompact className='MasonDataGridContainer-header-menuitem-icon' />,
                    selected: activeDensity === 'compact',
                    text: 'Compact',
                  },
                  {
                    id: 'standard',
                    icon: <UxTableDensityStandard className='MasonDataGridContainer-header-menuitem-icon' />,
                    selected: activeDensity === 'standard',
                    text: 'Standard',
                  },
                  {
                    id: 'comfortable',
                    icon: (
                      <UxTableDensityComfortable className='MasonDataGridContainer-header-menuitem-icon' />
                    ),
                    selected: activeDensity === 'comfortable',
                    text: 'Comfortable',
                  },
                ],
              },
            ]}
          />
        </div>
        {children}
        {isScrolledToBottom && activeGridToggle.pagination && (
          <div className='MasonDataGridContainer-footer'>
            <PaginationZone />
          </div>
        )}
      </div>
    </Paper>
  );
});
